import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { CANONICAL_FIELDS } from "@/lib/inventory";
import { Upload, Loader2, ArrowRight, ArrowLeft, CheckCircle2, AlertTriangle, X, FileSpreadsheet, Download, Keyboard } from "lucide-react";
import POManualEntry from "@/components/POManualEntry";

const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500";

export default function POWizard({ accountId, onClose, onCreated }) {
  const fileRef = useRef(null);
  const [step, setStep] = useState(1);
  const [manual, setManual] = useState(false);
  const [busy, setBusy] = useState(false);
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [filePath, setFilePath] = useState(null);
  const [meta, setMeta] = useState({ customer_po_number: "", vendor: "", tracking_number: "", expected_arrival: "" });
  const [validated, setValidated] = useState([]);
  const [stats, setStats] = useState({ error_rows: 0, warning_rows: 0, total: 0 });

  const buildLines = () =>
    rows.map((r) => {
      const l = {};
      CANONICAL_FIELDS.forEach((f) => { l[f.key] = mapping[f.key] ? (r[mapping[f.key]] ?? "") : ""; });
      return l;
    });

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("account_id", accountId);
      const { data } = await api.post("/inventory/pos/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHeaders(data.headers);
      setRows(data.rows);
      setMapping(data.suggested_mapping || {});
      setFilePath(data.file_path);
      setMeta((m) => ({ ...m, customer_po_number: data.suggested_mapping?.po_number && data.rows[0]?.[data.suggested_mapping.po_number] || m.customer_po_number }));
      setStep(2);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const runValidate = async () => {
    const missingReq = CANONICAL_FIELDS.filter((f) => f.required && !mapping[f.key]);
    if (missingReq.length) { toast.error(`Map required columns: ${missingReq.map((f) => f.label).join(", ")}`); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/inventory/pos/validate", { lines: buildLines() });
      setValidated(data.lines);
      setStats({ error_rows: data.error_rows, warning_rows: data.warning_rows, total: data.total });
      setStep(3);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const editLine = (idx, key, val) => setValidated((arr) => arr.map((l, i) => (i === idx ? { ...l, [key]: val } : l)));

  // Big files: render the rows that need attention first, then a slice of the clean ones.
  // All rows are still validated and submitted — this only keeps the table responsive.
  const MAX_SHOWN = 300;
  const flagged = validated.map((l, i) => ({ l, i })).filter(({ l }) => l.errors?.length || l.warnings?.length);
  const clean = validated.map((l, i) => ({ l, i })).filter(({ l }) => !l.errors?.length && !l.warnings?.length);
  const shownRows = [...flagged, ...clean].slice(0, MAX_SHOWN);

  const revalidate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/inventory/pos/validate", { lines: validated.map(({ errors, warnings, ...l }) => l) });
      setValidated(data.lines);
      setStats({ error_rows: data.error_rows, warning_rows: data.warning_rows, total: data.total });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (stats.error_rows > 0) { toast.error("Fix all error rows first"); return; }
    setBusy(true);
    try {
      const lines = validated.map(({ errors, warnings, ...l }) => l);
      await api.post("/inventory/pos", { account_id: accountId, ...meta, file_path: filePath, lines, submit: true });
      toast.success("PO submitted to BoxHero");
      onCreated?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return createPortal((
    <div className="fixed inset-0 z-[95] bg-black/50 flex items-center justify-center p-4" data-testid="po-wizard">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-slate-900">New inbound PO</h2>
            <span className="text-xs text-slate-400">{manual ? "Type it in" : `Step ${step} of 3`}</span>
          </div>
          <button onClick={onClose} data-testid="po-wizard-close" className="text-slate-400 hover:text-slate-700 p-1"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {manual && (
            <POManualEntry
              accountId={accountId}
              onBack={() => setManual(false)}
              onCreated={() => { onCreated?.(); onClose(); }}
            />
          )}

          {!manual && step === 1 && (
            <div className="text-center py-8" data-testid="po-step-upload">
              <div onClick={() => fileRef.current?.click()} className="cursor-pointer border-2 border-dashed border-slate-300 hover:border-blue-400 rounded-2xl p-12 transition-colors">
                {busy ? <Loader2 className="w-10 h-10 mx-auto text-blue-500 animate-spin" /> : <Upload className="w-10 h-10 mx-auto text-slate-400" />}
                <p className="mt-4 text-lg font-semibold text-slate-700">Upload your PO file</p>
                <p className="text-sm text-slate-500 mt-1">Excel (.xlsx) or CSV — we&apos;ll auto-detect the columns.</p>
              </div>
              <input ref={fileRef} type="file" accept=".xlsx,.csv" onChange={(e) => onFile(e.target.files?.[0])} className="hidden" data-testid="po-file-input" />
              <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3">
                <button onClick={() => window.open(`${API_BASE}/inventory/pos/template`, "_blank", "noopener")} data-testid="po-wizard-template" className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700">
                  <Download className="w-4 h-4" /> Download the CSV template
                </button>
                <span className="hidden sm:inline text-slate-300">|</span>
                <button onClick={() => setManual(true)} data-testid="po-type-in-btn" className="inline-flex items-center gap-2 border border-slate-300 hover:border-blue-400 hover:text-blue-700 text-slate-800 font-semibold px-4 py-2 rounded-md text-sm transition-colors">
                  <Keyboard className="w-4 h-4" /> Type in the PO
                </button>
              </div>
              <p className="mt-2 text-xs text-slate-400">Only a couple of products? Skip the spreadsheet and type it in — works on your phone.</p>
            </div>
          )}

          {!manual && step === 2 && (
            <div className="space-y-5" data-testid="po-step-map">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[["customer_po_number", "PO number"], ["vendor", "Vendor"], ["tracking_number", "Tracking #"], ["expected_arrival", "Expected arrival"]].map(([k, l]) => (
                  <div key={k}>
                    <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1">{l}</label>
                    <input type={k === "expected_arrival" ? "date" : "text"} value={meta[k]} onChange={(e) => setMeta((m) => ({ ...m, [k]: e.target.value }))} data-testid={`po-meta-${k}`} className={inputCls} />
                  </div>
                ))}
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-800 mb-2">Map your columns</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {CANONICAL_FIELDS.map((f) => (
                    <div key={f.key} className="flex items-center gap-2">
                      <span className="w-40 text-sm text-slate-600">{f.label}{f.required && <span className="text-red-500"> *</span>}</span>
                      <select value={mapping[f.key] || ""} onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value }))} data-testid={`po-map-${f.key}`} className={inputCls + " bg-white"}>
                        <option value="">— none —</option>
                        {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-xs text-slate-400">{rows.length.toLocaleString()} rows detected — every row in the file is read, blanks are skipped.</p>
              {mapping.customer_location && (
                <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="po-proposed-note">
                  Proposed location is mapped — each product will be assigned that bin, and receivers will be told where to put it without scanning a bin. Products already stored somewhere keep their existing bin.
                </p>
              )}
            </div>
          )}

          {!manual && step === 3 && (
            <div className="space-y-4" data-testid="po-step-validate">
              <div className="flex gap-3">
                <div className="flex items-center gap-2 text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"><span className="font-bold">{stats.total}</span> rows</div>
                <div className={`flex items-center gap-2 text-sm rounded-lg px-3 py-2 border ${stats.error_rows ? "bg-red-50 border-red-200 text-red-700" : "bg-emerald-50 border-emerald-200 text-emerald-700"}`}>
                  {stats.error_rows ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />} {stats.error_rows} errors
                </div>
                {stats.warning_rows > 0 && <div className="flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2"><AlertTriangle className="w-4 h-4" /> {stats.warning_rows} warnings</div>}
                <button onClick={revalidate} disabled={busy} data-testid="po-recheck" className="ml-auto text-sm font-semibold text-blue-600 hover:text-blue-700">Re-check</button>
              </div>
              <div className="overflow-x-auto border border-slate-200 rounded-lg max-h-[45vh]">
                <table className="w-full text-left text-sm" data-testid="po-validate-table">
                  <thead className="sticky top-0 bg-slate-50">
                    <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <th className="py-2 px-3 font-medium">SKU</th><th className="py-2 px-3 font-medium">Barcode</th><th className="py-2 px-3 font-medium">Qty</th><th className="py-2 px-3 font-medium">Issues</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shownRows.map(({ l, i }) => (
                      <tr key={i} className={`border-b border-slate-100 ${l.errors?.length ? "bg-red-50/40" : ""}`} data-testid={`po-vrow-${i}`}>
                        <td className="py-1.5 px-3"><input data-testid={`po-vrow-${i}-sku`} value={l.sku || ""} onChange={(e) => editLine(i, "sku", e.target.value)} className="w-44 px-2 py-1 border border-slate-200 rounded text-sm font-mono" /></td>
                        <td className="py-1.5 px-3"><input data-testid={`po-vrow-${i}-barcode`} value={l.barcode || ""} onChange={(e) => editLine(i, "barcode", e.target.value)} className="w-36 px-2 py-1 border border-slate-200 rounded text-sm font-mono" /></td>
                        <td className="py-1.5 px-3"><input data-testid={`po-vrow-${i}-qty`} value={l.expected_qty ?? ""} onChange={(e) => editLine(i, "expected_qty", e.target.value)} className="w-16 px-2 py-1 border border-slate-200 rounded text-sm" /></td>
                        <td className="py-1.5 px-3 text-xs">
                          {(l.errors || []).map((e, j) => <span key={`e-${j}-${e}`} className="inline-block text-red-600 mr-2">• {e}</span>)}
                          {(l.warnings || []).map((w, j) => <span key={`w-${j}-${w}`} className="inline-block text-amber-600 mr-2">⚠ {w}</span>)}
                          {!l.errors?.length && !l.warnings?.length && <span className="text-emerald-600">OK</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {shownRows.length < validated.length && (
                <p className="text-xs text-slate-500" data-testid="po-rows-truncated">
                  Showing {shownRows.length.toLocaleString()} of {validated.length.toLocaleString()} rows — every row with a problem is listed first, and all {validated.length.toLocaleString()} will be submitted.
                </p>
              )}
            </div>
          )}
        </div>

        <div className={`flex items-center justify-between px-6 py-4 border-t border-slate-100 ${manual ? "hidden" : ""}`}>
          <button onClick={() => (step > 1 ? setStep(step - 1) : onClose())} data-testid="po-back-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800">
            <ArrowLeft className="w-4 h-4" /> {step > 1 ? "Back" : "Cancel"}
          </button>
          {step === 2 && <button onClick={runValidate} disabled={busy} data-testid="po-to-validate" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold px-5 py-2 rounded-md">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Validate</button>}
          {step === 3 && <button onClick={submit} disabled={busy || stats.error_rows > 0} data-testid="po-submit" className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold px-5 py-2 rounded-md">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Submit to BoxHero</button>}
        </div>
      </div>
    </div>
  ), document.body);
}
